import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { SEOHead } from '@/components/SEO';
import { ArticleSchema } from '@/components/Schema';
import { blogService, type BlogPost as BlogPostType, type BlogMeta } from '@/services/blogService';
import { BlogTemplate } from '@/components/blog';

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPostType | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<BlogMeta[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPost = async () => {
      if (!slug) return;
      
      setLoading(true);
      try {
        const blogPost = await blogService.getPostBySlug(slug);
        if (blogPost) {
          setPost(blogPost);
          const related = await blogService.getRelatedPosts(slug);
          setRelatedPosts(related);
        }
      } catch (error) {
        console.error('Error loading blog post:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPost();
    window.scrollTo(0, 0);
  }, [slug]);

  const sharePost = () => {
    if (navigator.share) {
      navigator.share({
        title: post?.title,
        text: post?.excerpt,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="container mx-auto px-6 py-32 text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Post Not Found</h1>
          <p className="text-white/70 mb-8">The blog post you're looking for doesn't exist.</p>
          <Link to="/blog">
            <Button className="bg-white text-black hover:bg-zinc-200">
              Back to Blog
            </Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title={post.title}
        description={post.excerpt}
        keywords={post.tags}
        canonicalUrl={`https://www.fortifytek.com/blog/${slug}`}
        ogImage={post.image}
        schemaType="Article"
        author={post.author}
        publishDate={post.date}
        modifiedDate={post.date}
        breadcrumbs={[
          { name: 'Home', url: 'https://www.fortifytek.com/' },
          { name: 'Blog', url: 'https://www.fortifytek.com/blog' },
          { name: post.title, url: `https://www.fortifytek.com/blog/${slug}` }
        ]}
      />
      <ArticleSchema
        article={{
          headline: post.title,
          description: post.excerpt,
          url: `https://www.fortifytek.com/blog/${slug}`,
          image: post.image,
          author: post.author,
          datePublished: post.date,
          dateModified: post.date,
          keywords: post.tags,
          category: post.category || 'Technology',
          tags: post.tags,
          wordCount: post.content?.length || 1000,
          readingTime: `${Math.ceil((post.content?.length || 1000) / 200)}M`,
          isAccessibleForFree: true
        }}
      />

      <Navigation />
      
      {/* Back to Blog Link */}
      <div className="fixed top-24 left-6 z-40">
        <Link to="/blog" className="inline-flex items-center text-amber-400 hover:text-amber-300 transition-colors bg-zinc-900/90 backdrop-blur-xl px-4 py-2 rounded-full border border-zinc-800">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Blog
        </Link>
      </div>

      <BlogTemplate post={post} relatedPosts={relatedPosts} />
      
      <Footer />
    </>
  );
};

export default BlogPost;